import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { Search, Loader2, Users, UserCheck, TrendingUp, Coins } from 'lucide-react';
import ReferralProgramConfigCard from './ReferralProgramConfigCard';
import ReferralPayoutSummary from './ReferralPayoutSummary';


interface AdminReferral {
  id: string;
  created_at: string;
  converted_at: string | null;
  status: string;
  referral_code: string | null;
  purchase_amount: number | null;
  purchase_currency: string | null;
  referrer_user_id: string;
  referrer_name: string | null;
  referrer_email: string | null;
  referrer_org: string | null;
  referred_user_id: string | null;
  referred_name: string | null;
  referred_email: string | null;
  commission_amount: number | null;
  commission_status: string | null;
  commission_id: string | null;
  reward_code: string | null;
}

const statusLabel: Record<string, string> = {
  pending: 'Pendiente',
  registered: 'Registrado',
  converted: 'Convertido',
};

export default function ReferralsTrackingTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [selected, setSelected] = useState<string[]>([]);


  const { data: referrals = [], isLoading } = useQuery({
    queryKey: ['admin_referrals'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('admin_list_referrals');
      if (error) throw error;
      return (data ?? []) as AdminReferral[];
    },
  });

  const setCommission = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await (supabase as any).rpc('admin_set_commission_status', {
        _commission_id: id,
        _status: status,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin_referrals'] });
      toast({ title: 'Comisión actualizada' });
    },
    onError: () => toast({ title: 'No se pudo actualizar la comisión', variant: 'destructive' }),
  });

  const bulkSetCommissions = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      const { error } = await (supabase as any).rpc('admin_set_commissions_status', {
        _ids: ids,
        _status: status,
      });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['admin_referrals'] });
      qc.invalidateQueries({ queryKey: ['admin_referral_summary'] });
      setSelected([]);
      toast({ title: `${vars.ids.length} comisiones actualizadas` });
    },
    onError: () => toast({ title: 'No se pudieron actualizar las comisiones', variant: 'destructive' }),
  });



  const kpis = useMemo(() => {
    const total = referrals.length;
    const registered = referrals.filter(r => r.status === 'registered' || r.status === 'converted').length;
    const converted = referrals.filter(r => r.status === 'converted').length;
    const revenue = referrals.reduce((acc, r) => acc + Number(r.purchase_amount ?? 0), 0);
    const pendingCommissions = referrals
      .filter(r => r.commission_status === 'pending')
      .reduce((acc, r) => acc + Number(r.commission_amount ?? 0), 0);
    return {
      total,
      registered,
      converted,
      rate: registered ? Math.round((converted / registered) * 100) : 0,
      revenue,
      pendingCommissions,
    };
  }, [referrals]);

  const filtered = referrals.filter(r => {
    if (statusFilter !== '__all__' && r.status !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [r.referral_code, r.referrer_name, r.referrer_email, r.referred_name, r.referred_email, r.referrer_org]
      .some(v => (v ?? '').toLowerCase().includes(q));
  });

  const fmt = (n: number) => n.toLocaleString('es-CO', { maximumFractionDigits: 0 });

  const payableIds = filtered.filter(r => r.commission_id && r.commission_status === 'pending').map(r => r.commission_id!);
  const allSelected = payableIds.length > 0 && payableIds.every(id => selected.includes(id));
  const toggleAll = () => setSelected(allSelected ? [] : payableIds);
  const toggleOne = (id: string) =>
    setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  return (
    <div className="space-y-4">

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Card><CardContent className="pt-5 flex items-center gap-3">
          <Users className="w-5 h-5 text-primary" />
          <div><p className="text-2xl font-bold">{kpis.total}</p><p className="text-xs text-muted-foreground">Referidos totales</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-5 flex items-center gap-3">
          <UserCheck className="w-5 h-5 text-emerald-500" />
          <div><p className="text-2xl font-bold">{kpis.converted}</p><p className="text-xs text-muted-foreground">Convertidos</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-5 flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-amber-500" />
          <div><p className="text-2xl font-bold">{kpis.rate}%</p><p className="text-xs text-muted-foreground">Tasa de conversión</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-5 flex items-center gap-3">
          <Coins className="w-5 h-5 text-primary" />
          <div><p className="text-2xl font-bold">{fmt(kpis.revenue)}</p><p className="text-xs text-muted-foreground">Ingresos referidos</p></div>
        </CardContent></Card>
      </div>

      <ReferralProgramConfigCard />
      <ReferralPayoutSummary />



      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar por código, referidor o referido…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[170px]"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos</SelectItem>
            <SelectItem value="pending">Pendiente</SelectItem>
            <SelectItem value="registered">Registrado</SelectItem>
            <SelectItem value="converted">Convertido</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">{filtered.length} de {referrals.length} referidos</p>
        {selected.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{selected.length} seleccionadas</span>
            <Button size="sm" variant="outline" disabled={bulkSetCommissions.isPending}
              onClick={() => bulkSetCommissions.mutate({ ids: selected, status: 'cancelled' })}>
              Anular
            </Button>
            <Button size="sm" disabled={bulkSetCommissions.isPending}
              onClick={() => bulkSetCommissions.mutate({ ids: selected, status: 'paid' })}>
              Marcar pagadas
            </Button>
          </div>
        )}
      </div>


      {isLoading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} disabled={payableIds.length === 0} aria-label="Seleccionar todas" />
                </TableHead>
                <TableHead>Referidor</TableHead>

                <TableHead>Referido</TableHead>
                <TableHead>Código</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Compra</TableHead>
                <TableHead>Recompensa</TableHead>
                <TableHead>Comisión</TableHead>
                <TableHead className="w-32">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(r => (
                <TableRow key={r.id}>
                  <TableCell>
                    {r.commission_id && r.commission_status === 'pending' && (
                      <Checkbox
                        checked={selected.includes(r.commission_id)}
                        onCheckedChange={() => toggleOne(r.commission_id!)}
                        aria-label="Seleccionar comisión"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{r.referrer_name || '—'}</p>

                    <p className="text-xs text-muted-foreground">{r.referrer_email}</p>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{r.referred_name || '—'}</p>
                    <p className="text-xs text-muted-foreground">{r.referred_email}</p>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.referral_code || '—'}</TableCell>
                  <TableCell>
                    <Badge variant={r.status === 'converted' ? 'default' : 'secondary'}>
                      {statusLabel[r.status] ?? r.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{r.purchase_amount ? `${fmt(Number(r.purchase_amount))} ${r.purchase_currency ?? ''}` : '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{r.reward_code || '—'}</TableCell>
                  <TableCell>
                    {r.commission_amount != null ? (
                      <span>{fmt(Number(r.commission_amount))} <span className="text-xs text-muted-foreground">({r.commission_status})</span></span>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    {r.commission_id && r.commission_status === 'pending' && (
                      <Button size="sm" variant="outline" onClick={() => setCommission.mutate({ id: r.commission_id!, status: 'paid' })}>
                        Marcar pagada
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Sin referidos</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Comisiones pendientes: {fmt(kpis.pendingCommissions)}
      </p>
    </div>
  );
}
